import { useState, useRef, useCallback } from 'react';

/**
 * Options for configuring error message behavior
 */
export interface UseErrorMessageOptions {
    /** Duration in milliseconds before auto-clearing the error (default: 10000) */
    autoClearDelay?: number;
    /** Duration in milliseconds before focusing on error element (default: 100) */
    focusDelay?: number;
    /** Whether to auto-scroll to error message (default: true) */
    autoScroll?: boolean;
}

/**
 * Return value from useErrorMessage hook
 */
export interface UseErrorMessageResult {
    /** Current error message, null if no error */
    errorMessage: string | null;
    /** Ref to attach to error message container for auto-scroll/focus */
    errorMessageRef: React.RefObject<HTMLDivElement>;
    /** Function to set a new error message */
    setError: (message: string) => void;
    /** Function to manually clear the error message */
    clearError: () => void;
}

/**
 * Custom hook for managing error messages with auto-clear and accessibility features
 * 
 * Features:
 * - Auto-clear after configurable delay
 * - Auto-scroll to error message
 * - Auto-focus for screen readers
 * - Manual clear capability
 * 
 * @param options Configuration options for error behavior
 * @returns Error state and control functions
 * 
 * @example
 * ```tsx
 * const { errorMessage, errorMessageRef, setError, clearError } = useErrorMessage();
 * 
 * // Display error
 * <div ref={errorMessageRef} role="alert">
 *   {errorMessage}
 * </div>
 * 
 * // Set error from handler
 * setError("Something went wrong");
 * ```
 */
export const useErrorMessage = (
    options: UseErrorMessageOptions = {}
): UseErrorMessageResult => {
    const {
        autoClearDelay = 10000,
        focusDelay = 100,
        autoScroll = true
    } = options;

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const errorMessageRef = useRef<HTMLDivElement>(null);
    const autoClearTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    /**
     * Clears any existing auto-clear timeout
     */
    const clearAutoClearTimeout = useCallback(() => {
        if (autoClearTimeoutRef.current) {
            clearTimeout(autoClearTimeoutRef.current);
            autoClearTimeoutRef.current = null;
        }
    }, []);

    /**
     * Manually clears the error message
     */
    const clearError = useCallback(() => {
        setErrorMessage(null);
        clearAutoClearTimeout();
    }, [clearAutoClearTimeout]);

    /**
     * Sets a new error message with auto-scroll, focus, and auto-clear
     */
    const setError = useCallback((message: string) => {
        // Clear any existing timeout
        clearAutoClearTimeout();

        // Set the new error message
        setErrorMessage(message);

        // Auto-scroll and focus for accessibility
        if (autoScroll) {
            setTimeout(() => {
                if (errorMessageRef.current) {
                    errorMessageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                    errorMessageRef.current.focus();
                }
            }, focusDelay);
        }

        // Auto-clear after delay
        autoClearTimeoutRef.current = setTimeout(() => {
            setErrorMessage(null);
            autoClearTimeoutRef.current = null;
        }, autoClearDelay);
    }, [autoClearDelay, focusDelay, autoScroll, clearAutoClearTimeout]);

    return {
        errorMessage,
        errorMessageRef,
        setError,
        clearError
    };
};
